import { Fragment, type MouseEvent, type ReactNode } from 'react';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useMenu } from '@core/hooks/useMenu';
import ActionButton from './ActionButton';
import BrandLoader from './BrandLoader';
import ContextMenu, { type ContextMenuItem } from './ContextMenu';
import SelectableListItem from './SelectableListItem';
import { DragHandle, SortableItem, SortableList } from './Sortable';
import TwoPaneLayout from './TwoPaneLayout';

type ListDetailItem = { id: number; name: string };

export interface ListDetailPaneProps<T extends ListDetailItem> {
  items: T[];
  selected: T | null;
  onSelect: (id: number) => void;
  /** Labels the select that replaces the list on mobile. */
  selectLabel: string;
  renderSecondary?: (item: T) => ReactNode;
  getMenuItems?: (item: T) => ContextMenuItem[];
  add?: { label: string; onClick: () => void; size?: 'xs' | 'sm' };
  /** Makes the list sortable by drag handle. */
  onReorder?: (items: T[]) => void;
  loading?: boolean;
  emptyState: { title?: string; description: string };
  sidebarWidth?: number;
  /** Details of the selected item. */
  children: ReactNode;
}

function ListDetailPane<T extends ListDetailItem>({
  items,
  selected,
  onSelect,
  selectLabel,
  renderSecondary,
  getMenuItems,
  add,
  onReorder,
  loading = false,
  emptyState,
  sidebarWidth,
  children,
}: ListDetailPaneProps<T>) {
  const menu = useMenu<ContextMenuItem[]>();

  const openItemMenu = getMenuItems
    ? (event: MouseEvent<HTMLElement>, item: T) =>
        menu.openMenu(event, getMenuItems(item))
    : undefined;

  const renderListItem = (item: T) => (
    <SelectableListItem
      title={item.name}
      secondary={renderSecondary?.(item)}
      leading={onReorder ? <DragHandle /> : undefined}
      selected={item.id === selected?.id}
      onClick={() => onSelect(item.id)}
      onMenuClick={
        openItemMenu ? (event) => openItemMenu(event, item) : undefined
      }
    />
  );

  const list = onReorder ? (
    <SortableList items={items} onReorder={onReorder}>
      {items.map((item) => (
        <SortableItem key={item.id} id={item.id} dragHandle>
          {renderListItem(item)}
        </SortableItem>
      ))}
    </SortableList>
  ) : (
    items.map((item) => (
      <Fragment key={item.id}>{renderListItem(item)}</Fragment>
    ))
  );

  const mobileHeader = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField
        select
        size="small"
        label={selectLabel}
        value={selected ? String(selected.id) : ''}
        onChange={(event) => onSelect(Number(event.target.value))}
        sx={{ flex: 1 }}
      >
        {items.map((item) => (
          <MenuItem key={item.id} value={String(item.id)}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
      {selected && openItemMenu ? (
        <IconButton
          size="small"
          aria-label={`${selected.name} options`}
          onClick={(event) => openItemMenu(event, selected)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ) : null}
      {add ? (
        <IconButton size="small" aria-label={add.label} onClick={add.onClick}>
          <AddIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );

  const addSize = add?.size ?? 'sm';
  const sidebarFooter = add ? (
    <ActionButton
      variant="ghost"
      size={addSize}
      icon={<AddIcon />}
      onClick={add.onClick}
      fullWidth
    >
      {add.label}
    </ActionButton>
  ) : undefined;

  const empty = (
    <Box
      sx={{
        flex: 1,
        minHeight: 200,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 2,
        textAlign: 'center',
      }}
    >
      {loading ? (
        <BrandLoader />
      ) : (
        <>
          {emptyState.title ? (
            <Typography variant="subtitle1" fontWeight={600}>
              {emptyState.title}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            {emptyState.description}
          </Typography>
          {add ? (
            <ActionButton
              variant="primary"
              size={addSize}
              icon={<AddIcon />}
              onClick={add.onClick}
            >
              {add.label}
            </ActionButton>
          ) : null}
        </>
      )}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {items.length === 0 ? (
          empty
        ) : (
          <TwoPaneLayout
            sidebar={list}
            sidebarFooter={sidebarFooter}
            mobileHeader={mobileHeader}
            sidebarWidth={sidebarWidth}
          >
            {children}
          </TwoPaneLayout>
        )}
      </Box>
      {openItemMenu ? (
        <ContextMenu
          open={menu.isOpen}
          anchorEl={menu.anchorEl}
          items={menu.item ?? []}
          onClose={menu.closeMenu}
        />
      ) : null}
    </>
  );
}

export default ListDetailPane;
